<template>
  <div class="main main__container">
    <p v-if="hasSubmitted" class="form__confirmation" @submit.prevent="submit">
      {{ model.confirmationText }}
    </p>
    <validation-observer
      v-else
      ref="observer"
      tag="oie-form-wrapper"
      class="information-island__form-container inputs-container"
      @submit="submit">
      <oie-base-input-v2
        id="customerNumber"
        v-model="form.customerNumber"
        :label="tStatic('customerNumberLabel')"
        :placeholder="tStatic('customerNumberLabel')">
      </oie-base-input-v2>

      <validation-provider
        rules="required"
        slim
        mode="passive">
        <template slot-scope="{ errors }">
          <oie-base-input-v2
            v-model="form.name"
            :label="tStatic('companyNameLabel')"
            required
            :placeholder="tStatic('companyNamePlaceholder')"
            :errors="errors"></oie-base-input-v2>
        </template>
      </validation-provider>

      <validation-provider
        rules="required"
        slim
        mode="passive">
        <template slot-scope="{ errors }">
          <oie-base-input-v2
            v-model="form.orgNumber"
            :label="model.organisationNumberLabel"
            required
            :placeholder="model.organisationNumberPlaceholder"
            :errors="errors"></oie-base-input-v2>
        </template>
      </validation-provider>

      <validation-provider
        rules="required"
        slim
        mode="passive">
        <template slot-scope="{ errors }">
          <oie-base-input-v2
            v-model="form.invoiceAddress.addressLine1"
            :label="tStatic('addressStreetLabel')"
            required
            :placeholder="tStatic('addressStreetPlaceholder')"
            :errors="errors">
          </oie-base-input-v2>
        </template>
      </validation-provider>

      <validation-provider
        :rules="createPostalCodeValidationRule"
        slim
        mode="passive">
        <template slot-scope="{ errors }">
          <oie-base-input-v2
            v-model="form.invoiceAddress.postalCode"
            :mask="createPostalCodeMask"
            :label="tStatic('addressPostalLabel')"
            required
            :placeholder="tStatic('addressPostalPlaceholder')"
            :errors="errors">
          </oie-base-input-v2>
        </template>
      </validation-provider>

      <validation-provider
        rules="required|invalidchars"
        slim
        mode="passive">
        <template slot-scope="{ errors }">
          <oie-base-input-v2
            v-model="form.invoiceAddress.city"
            :label="tStatic('addressCityLabel')"
            required
            :placeholder="tStatic('addressCityPlaceholder')"
            :errors="errors">
          </oie-base-input-v2>
        </template>
      </validation-provider>

      <validation-provider
        v-slot="{errors}"
        rules="required"
        slim>
        <oie-base-dropdown
          v-model="form.invoiceAddress.countryCode"
          :is-disabled="!hasMultipleMarkets"
          :label-text="`${tStatic('addressCountryLabel')}*`"
          :options="countryList"
          :initial-text="tStatic('addressCountryLabel')"
          :use-filter-functionality="countryList.length > 10"
          :filter-input-placeholder="tStatic('addressCountryPlaceholder')"
          :error-messages="errors">
        </oie-base-dropdown>
      </validation-provider>

      <validation-provider
        rules="required"
        slim
        mode="passive">
        <template slot-scope="{ errors }">
          <oie-base-input-v2
            v-model="form.contactPerson"
            :label="tStatic('fullName')"
            required
            :placeholder="tStatic('fullNamePlaceholder')"
            :errors="errors">
          </oie-base-input-v2>
        </template>
      </validation-provider>

      <validation-provider
        v-slot="{ errors }"
        rules="required|mobile:^[0-9+]+$"
        slim
        mode="lazy">
        <oie-phone-input
          v-model="form.phone"
          required
          :errors="errors"
          :country-code="selectedActiveCountry ? selectedActiveCountry.callingCode : null"
          :label="tStatic('phoneLabel')"
          :placeholder="tStatic('phonePlaceholderLabel')"
          maxlength="27">
        </oie-phone-input>
      </validation-provider>

      <validation-provider
        rules="required|email"
        slim
        mode="passive">
        <template slot-scope="{ errors }">
          <oie-base-input-v2
            v-model="form.email"
            :label="tStatic('email')"
            required
            :placeholder="tStatic('email')"
            maxlength="100"
            :errors="errors"></oie-base-input-v2>
        </template>
      </validation-provider>

      <validation-provider
        rules="required"
        slim
        mode="passive">
        <template slot-scope="{ errors}">
          <oie-base-textarea
            v-model="form.subject"
            :label="tStatic('errand')"
            :placeholder="tStatic('errandPlaceholder')"
            :errors="errors"
            maxlength="500"></oie-base-textarea>
        </template>
      </validation-provider>

      <validation-provider
        v-slot="{ errors }"
        rules="checked"
        slim
        mode="passive">
        <oie-base-radio-checkbox
          v-model="form.termsAccepted"
          :errors="errors"
          class="input-colum__checkbox"
          required
          :label="tStatic('approveTerms')"
          :description="tStatic('approveTermsLabel')"></oie-base-radio-checkbox>
      </validation-provider>

      <validation-provider
        v-slot="{errors}"
        rules="required"
        mode="passive"
        slim>
        <oie-recaptcha-v2
          ref="recaptcha"
          v-model="form.recaptchaResponse"
          :errors="errors">
        </oie-recaptcha-v2>
      </validation-provider>

      <oie-base-action
        v-element-loader="{ endpoint: 'forms/sendcontractapplicantform' }"
        type="submit"
        size="large">
        {{ tStatic('sendButton') }}
      </oie-base-action>
    </validation-observer>
  </div>
</template>

<script>
import httpService from '@/core/http-service'
import countryHandling from '@/mixins/country-handling'
import _ from 'lodash'

export default {

  mixins: [countryHandling],
  props: {
    model: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      form: {
        contactPerson: '',
        termsAccepted: false,
        customerNumber: '',
        email: '',
        invoiceAddress: {
          addressLine1: '',
          city: '',
          countryName: '',
          countryCode: '',
          name: '',
          postalCode: ''
        },
        name: '',
        phone: '',
        orgNumber: '',
        subject: '',
        recaptchaResponse: null
      },
      hasSubmitted: false,
      hasError: false
    }
  },
  computed: {
    createPostalCodeValidationRule() {
      return this.selectedCountry ? `required|postalCode:${this.selectedCountry.value},${this.selectedCountry.postalCodeRule}` : null
    },
    createPostalCodeMask() {
      return this.selectedCountry ? this.selectedCountry.postalCodeMask : null
    }
  },
  watch: {
    'form.invoiceAddress.countryCode': function (newVal, oldVal) {
      if(newVal) this.setActiveCountry(newVal)
    }
  },
  async beforeMount() {
    if(this.clientModel.isAuthenticated) {
      const userInfo = (await httpService.get('forms/currentuserinfo')).data
      _.merge(this.form, userInfo)
    } else {
      if(this.defaultCountry) this.form.invoiceAddress.countryCode = this.defaultCountry.value
    }
  },
  methods: {
    async submit() {
      const valid = await this.$refs.observer.validate()
      if (valid) {
        const response = await httpService.post('forms/sendcontractapplicantform', { ...this.form,
          blockId: this.blockId
        })

        if (response.data.success) {
          this.hasSubmitted = true
          this.$refs.observer.reset()
          this.form = { ...this.initialForm }
          this.$refs.recaptcha.reset()
        } else {
          // Handle error case if needed
          this.hasError = true
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.form__confirmation {
  font-size: 1.50rem;
  line-height: 1.75rem;
  display: inline-block;
  padding: 1.5rem 2rem;
  width: auto;

  border: 0.3rem solid #298651;

}
</style>
